import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { patients as patientsApi, visits as visitsApi } from '@/api/endpoints'
import { ApiError } from '@/api/errors'
import { queryKeys } from '@/api/queryKeys'
import type {
  BloodGroup,
  PatientResponse,
  PatientWriteRequest,
  VisitWriteRequest,
} from '@/api/types'
import { Banner } from '@/components/Banner'
import { Button } from '@/components/Button'
import { DateField } from '@/components/DateField'
import { Field } from '@/components/Field'
import { Select } from '@/components/Select'
import { Icon } from '@/components/Icon'
import { Spinner } from '@/components/Spinner'
import { TextArea } from '@/components/TextArea'
import { TextField } from '@/components/TextField'
import { useToast } from '@/components/Toast'
import { PhoneField } from '@/components/PhoneField'
import { AttachmentUploader, type UploaderHandle } from '@/features/attachments/AttachmentUploader'
import { useSession } from '@/features/auth/SessionProvider'
import {
  BLOOD_GROUP_OPTIONS,
  SEX_SELECT_OPTIONS,
  VISIT_TYPE_OPTIONS,
} from '@/lib/clinical'
import { formatTimeOfDay, todayIso } from '@/lib/dates'
import { formatIdentityLine } from '@/lib/age'
import { cx } from '@/lib/cx'
import { PrescriptionTable, emptyItem } from './PrescriptionTable'
import { TestResultTable, emptyTestResult } from './TestResultTable'
import { useVisitDraft } from './useDraft'
import { useFlushOnHide } from './useFlushOnHide'
import { errorMessage } from '@/api/messages'

const NOTES_MAX = 20000

function blankContent(): VisitWriteRequest {
  return {
    visit_date: todayIso(),
    visit_type: 'consultation',
    chief_complaints: null,
    notes: null,
    next_visit_date: null,
    test_results: [],
    prescription: [],
  }
}

interface Props {
  patient: PatientResponse
  /** Present when an existing consultation is being corrected. */
  visitId: string | null
}

/**
 * What the doctor writes on the left, what they order and attach on the right.
 * Patient identity is editable here on purpose: a wrong phone number surfaces
 * mid-consultation, and leaving the screen to fix it is how it stays wrong.
 */
export function ConsultationPanel({ patient, visitId }: Props) {
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { user } = useSession()
  const complaintsRef = useRef<HTMLTextAreaElement>(null)
  const uploader = useRef<UploaderHandle | null>(null)

  const [content, setContent] = useState<VisitWriteRequest>(blankContent)
  const [identity, setIdentity] = useState<PatientWriteRequest>(() => toPatientForm(patient))
  const [version, setVersion] = useState<number | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  // Identity is entered once and edited rarely, so it starts folded away: the
  // record header above already says who this is, and the consultation is what
  // the screen is for. Still editable here — correcting a phone number mid-
  // consult should not mean leaving the half-typed note behind.
  const [identityOpen, setIdentityOpen] = useState(false)
  const idempotencyKey = useMemo(() => crypto.randomUUID(), [])

  const existing = useQuery({
    queryKey: queryKeys.visit(visitId ?? 'none'),
    queryFn: () => visitsApi.get(visitId!),
    enabled: Boolean(visitId),
  })

  const draft = useVisitDraft({
    userId: user?.id ?? null,
    patientId: patient.id,
    visitId: visitId ?? 'new',
  })

  useEffect(() => {
    const loaded = existing.data
    if (!loaded) return
    setVersion(loaded.data.version)
    setContent({
      visit_date: loaded.data.visit_date,
      visit_type: loaded.data.visit_type,
      chief_complaints: loaded.data.chief_complaints,
      notes: loaded.data.notes,
      next_visit_date: loaded.data.next_visit_date,
      test_results: loaded.data.test_results.map((t) => ({
        test_name: t.test_name,
        result: t.result,
        recorded_on: t.recorded_on,
      })),
      prescription: loaded.data.prescription.map((item) => ({
        drug_name: item.drug_name,
        dose: item.dose,
        frequency: item.frequency,
        duration: item.duration,
        instructions: item.instructions,
      })),
    })
  }, [existing.data])

  useEffect(() => setIdentity(toPatientForm(patient)), [patient])

  // The complaint is the first thing written, so it takes focus.
  useEffect(() => {
    complaintsRef.current?.focus()
  }, [])

  const update = useCallback(
    (changes: Partial<VisitWriteRequest>) => {
      setContent((current) => {
        const next = { ...current, ...changes }
        draft.persist(next)
        return next
      })
    },
    [draft],
  )

  useFlushOnHide(() => draft.flush(content))

  const save = useMutation({
    mutationFn: async () => {
      // Identity first: if it changed, it is a correction the doctor just made
      // and it should stick even if the visit write then fails.
      if (identityChanged(patient, identity)) {
        await patientsApi.update(patient.id, identity)
      }
      const payload: VisitWriteRequest = {
        ...content,
        chief_complaints: content.chief_complaints?.trim() || null,
        notes: content.notes?.trim() || null,
        test_results: (content.test_results ?? []).filter((t) => t.test_name.trim()),
        prescription: (content.prescription ?? []).filter((item) => item.drug_name.trim()),
      }
      if (visitId && version !== null) return visitsApi.update(visitId, payload, version)
      return visitsApi.create(patient.id, payload, idempotencyKey)
    },
    onSuccess: async (result) => {
      await uploader.current?.flushQueued(result.data.id)
      await draft.clear()
      await queryClient.invalidateQueries({ queryKey: queryKeys.visits(patient.id) })
      await queryClient.invalidateQueries({ queryKey: queryKeys.patient(patient.id) })
      toast.show('Consultation saved')
      navigate(`/patients/${patient.id}`, { replace: true })
    },
    onError: (error) => {
      // Whatever was typed stays on screen. Nothing is discarded by an error path.
      if (error instanceof ApiError && error.isStaleWrite) {
        setSaveError(
          'This consultation was changed on another device since you opened it. Reload before saving again.',
        )
      } else if (error instanceof ApiError && error.isNetwork) {
        setSaveError(`${errorMessage(error)} Try again when it returns.`)
      } else if (error instanceof ApiError) {
        setSaveError(errorMessage(error))
      } else {
        setSaveError('Could not save. Your work is still here — try again.')
      }
    },
  })

  function copyFromLastVisit() {
    const previous = queryClient.getQueryData<{ pages: { data: { prescription: unknown[] }[] }[] }>(
      queryKeys.visits(patient.id),
    )
    const items = (previous?.pages?.[0]?.data?.[0]?.prescription ?? []) as VisitWriteRequest['prescription']
    if (!items || items.length === 0) {
      toast.show('No previous prescription to copy')
      return
    }
    update({ prescription: items.map((item) => ({ ...item })) })
  }

  if (visitId && existing.isLoading) return <Spinner label="Loading consultation" />

  const saving = save.isPending
  const identitySummary = [
    identity.op_number,
    identity.full_name,
    formatIdentityLine(identity.age_years ?? patient.age, identity.sex),
    identity.blood_group,
  ]
    .filter(Boolean)
    .join('  ·  ')

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        setSaveError(null)
        save.mutate()
      }}
      onKeyDown={(event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
          event.preventDefault()
          setSaveError(null)
          save.mutate()
        }
      }}
    >
      {draft.restored && (
        <div className="mb-4">
          <Banner
            tone="info"
            live
            actions={
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setContent(draft.restored!.content)
                    draft.dismissRestored()
                  }}
                >
                  Restore
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    void draft.clear()
                    draft.dismissRestored()
                  }}
                >
                  Discard
                </Button>
              </>
            }
          >
            Unsaved consultation from {formatTimeOfDay(draft.restored.savedAt)} on this device.
          </Banner>
        </div>
      )}

      {saveError && (
        <div className="mb-4">
          <Banner tone="danger" live>
            {saveError}
          </Banner>
        </div>
      )}

      <div className="grid items-start gap-4 min-[75rem]:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] min-[100rem]:grid-cols-2">
        <div className="@container flex min-w-0 flex-col gap-4">
          <section className="overflow-hidden rounded-lg border border-line-soft bg-surface">
            <button
              type="button"
              aria-expanded={identityOpen}
              aria-controls="patient-details"
              onClick={() => setIdentityOpen((o) => !o)}
              className="flex min-h-touch w-full items-center gap-3 px-5 py-3 text-left transition-colors duration-150 ease-standard hover:bg-hover"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
                  Patient details
                </span>
                <span className="block truncate text-sm text-fg-muted">{identitySummary}</span>
              </span>
              <span className="flex-none text-sm font-medium text-accent">
                {identityOpen ? 'Done' : 'Edit'}
              </span>
              <Icon
                name={identityOpen ? 'chevronUp' : 'chevronDown'}
                size="1.125rem"
                className="flex-none text-fg-subtle"
              />
            </button>

            <div id="patient-details" hidden={!identityOpen} className="border-t border-line-soft p-5">
              <div className="grid gap-4 @[560px]:grid-cols-2">
                <TextField
                  label="OP number"
                  value={identity.op_number}
                  readOnly
                  // Changing an OP number is a correction, not part of a
                  // consultation. It lives on the patient record.
                  title="Edit the OP number from the patient record"
                />
                <TextField
                  label="Name"
                  required
                  value={identity.full_name}
                  onChange={(e) => setIdentity({ ...identity, full_name: e.target.value })}
                />
              </div>

              <div className="mt-4 grid gap-4 @[560px]:grid-cols-3">
                <TextField
                  label="Age"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={130}
                  value={identity.age_years ?? patient.age ?? ''}
                  onChange={(e) =>
                    setIdentity({
                      ...identity,
                      age_years: e.target.value === '' ? null : Number(e.target.value),
                      date_of_birth: null,
                    })
                  }
                />
                <Field label="Sex">
                  {({ id, labelId }) => (
                    <Select
                      id={id}
                      labelledBy={labelId}
                      options={SEX_SELECT_OPTIONS}
                      value={identity.sex ?? 'unknown'}
                      onChange={(sex) => setIdentity({ ...identity, sex })}
                    />
                  )}
                </Field>
                <Field label="Blood group">
                  {({ id, labelId }) => (
                    <Select
                      id={id}
                      labelledBy={labelId}
                      options={BLOOD_GROUP_OPTIONS}
                      value={identity.blood_group ?? ''}
                      onChange={(group) =>
                        setIdentity({
                          ...identity,
                          blood_group: (group || null) as BloodGroup | null,
                        })
                      }
                    />
                  )}
                </Field>
              </div>

              <div className="mt-4 grid gap-4 @[560px]:grid-cols-2">
                <PhoneField
                  label="Phone number"
                  value={identity.phone ?? ''}
                  onChange={(phone) => setIdentity({ ...identity, phone })}
                />
                <TextArea
                  label="Address"
                  rows={2}
                  autoGrow={false}
                  value={identity.address ?? ''}
                  onChange={(e) => setIdentity({ ...identity, address: e.target.value })}
                />
              </div>
            </div>
          </section>

          {/* The hot path. Nothing above it now except one folded line, so the
              doctor types into the note rather than scrolling to find it. */}
          <section className="rounded-lg border border-line-soft bg-surface p-5">
            <h3 className="mb-4 text-lg font-semibold tracking-snug">Consultation</h3>

            <div className="grid gap-4">
              <TextArea
                ref={complaintsRef}
                label="Chief complaints"
                rows={2}
                value={content.chief_complaints ?? ''}
                onChange={(e) => update({ chief_complaints: e.target.value })}
                placeholder="Cough, mild fever, body ache for 3 days."
              />
              <TextArea
                label="Notes / clinical findings"
                rows={6}
                maxLength={NOTES_MAX}
                value={content.notes ?? ''}
                onChange={(e) => update({ notes: e.target.value })}
                placeholder="Mild congestion. No breathing difficulty."
              />
            </div>

            <div className="mt-4 grid gap-4 @[560px]:grid-cols-2">
              <Field label="Visit type">
                {({ id, labelId }) => (
                  <Select
                    id={id}
                    labelledBy={labelId}
                    options={VISIT_TYPE_OPTIONS}
                    value={content.visit_type}
                    onChange={(visit_type) => update({ visit_type })}
                  />
                )}
              </Field>
              <DateField
                label="Next visit"
                value={content.next_visit_date ?? ''}
                onChange={(iso) => update({ next_visit_date: iso || null })}
              />
            </div>
          </section>
        </div>

        <div className="@container flex min-w-0 flex-col gap-4">
          <section className="rounded-lg border border-line-soft bg-surface p-5">
            <div className="mb-3 flex min-h-9 flex-wrap items-center justify-between gap-x-3 gap-y-2">
              <h3 className="text-lg font-semibold tracking-snug">Test results</h3>
              <Button
                variant="outline"
                size="small"
                onClick={() =>
                  update({ test_results: [...(content.test_results ?? []), emptyTestResult()] })
                }
              >
                <Icon name="plus" size="1rem" />
                Add result
              </Button>
            </div>
            <TestResultTable
              items={content.test_results?.length ? content.test_results : [emptyTestResult()]}
              onChange={(items) => update({ test_results: items })}
            />
          </section>

          <section className="rounded-lg border border-line-soft bg-surface p-5">
            <div className="mb-3 flex min-h-9 flex-wrap items-center justify-between gap-x-3 gap-y-2">
              <h3 className="text-lg font-semibold tracking-snug">Prescription</h3>
              <span className="flex flex-wrap items-center gap-2">
                <Button variant="ghost" size="small" onClick={copyFromLastVisit}>
                  Copy last
                </Button>
                <Button
                  variant="outline"
                  size="small"
                  onClick={() =>
                    update({ prescription: [...(content.prescription ?? []), emptyItem()] })
                  }
                >
                  <Icon name="plus" size="1rem" />
                  Add medicine
                </Button>
              </span>
            </div>
            {/* One blank line is always ready to type into. An empty table
                cost a hunt for "Add medicine" before a single drug could be
                written, every consultation; blank rows are dropped on save. */}
            <PrescriptionTable
              items={content.prescription?.length ? content.prescription : [emptyItem()]}
              onChange={(items) => update({ prescription: items })}
            />
          </section>

          <section className="rounded-lg border border-line-soft bg-surface p-5">
            <div className="mb-3 flex min-h-9 flex-wrap items-center justify-between gap-x-3 gap-y-2">
              <h3 className="text-lg font-semibold tracking-snug">Attached documents</h3>
            </div>
            <AttachmentUploader
              visitId={visitId}
              existing={existing.data?.data.attachments ?? []}
              onReady={(handle) => {
                uploader.current = handle
              }}
            />
          </section>
        </div>

        {/* One save, pinned to the bottom of the viewport across both columns.
            It used to sit at the end of the left-hand card, which on a tall
            screen is the middle of the page and on a phone is a scroll away. */}
        <div
          className={cx(
            'sticky bottom-0 z-sticky -mx-4 flex flex-wrap items-center gap-3 border-t border-line-soft bg-surface px-4 py-3 shadow-[0_-4px_12px_-8px_rgb(0_0_0/0.25)]',
            'pb-safe-3 lg:-mx-6 lg:px-6 min-[75rem]:col-span-2',
          )}
        >
          <Button
            variant="secondary"
            onClick={() => {
              setContent(blankContent())
              void draft.clear()
            }}
          >
            Clear
          </Button>
          <span className="ml-auto flex items-center gap-3">
            {draft.savedAt && (
              <span className="text-xs text-fg-subtle">
                Draft saved {formatTimeOfDay(draft.savedAt)}
              </span>
            )}
            <Button type="submit" loading={saving}>
              <Icon name="save" />
              Save consultation
            </Button>
          </span>
        </div>
      </div>
    </form>
  )
}

function toPatientForm(patient: PatientResponse): PatientWriteRequest {
  return {
    op_number: patient.op_number,
    full_name: patient.full_name,
    date_of_birth: patient.date_of_birth,
    age_years: patient.date_of_birth ? null : patient.age,
    sex: patient.sex,
    phone: patient.phone,
    address: patient.address,
    blood_group: patient.blood_group,
  }
}

function identityChanged(patient: PatientResponse, form: PatientWriteRequest): boolean {
  return (
    form.full_name !== patient.full_name ||
    (form.phone ?? null) !== (patient.phone ?? null) ||
    (form.address ?? null) !== (patient.address ?? null) ||
    (form.sex ?? 'unknown') !== patient.sex ||
    (form.blood_group ?? null) !== (patient.blood_group ?? null) ||
    (form.age_years ?? null) !== (patient.date_of_birth ? null : patient.age)
  )
}
